/**
 * Provides keyboard and mouse event listeners for the listbox (role="listbox") section of a custom
 * select. See `JumpWeb.Components.Core.Select.listbox/1`
 */

import { Hook, makeHook } from './Hook'

const optionSelector = "[role='option']"

function isVisibleInContainer(elem, container) {
  const elemRect = elem.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()

  return elemRect.bottom < containerRect.bottom && elemRect.top > containerRect.top
}

export default makeHook(
  class SelectListBox extends Hook {
    activeId?: string

    mounted() {
      this.handleClick = this.handleClick.bind(this)
      this.handleKeyDown = this.handleKeyDown.bind(this)
      this.handleMouseOver = this.handleMouseOver.bind(this)

      this.getComboxbox()?.addEventListener('keydown', this.handleKeyDown)
      this.el.addEventListener('click', this.handleClick)
      this.el.addEventListener('mouseover', this.handleMouseOver)

      this.initActiveOption()
    }

    updated() {
      this.initActiveOption()
    }

    destroyed() {
      this.getComboxbox()?.removeEventListener('keydown', this.handleKeyDown)
      this.el.removeEventListener('click', this.handleClick)
      this.el.removeEventListener('mouseover', this.handleMouseOver)
    }

    getComboxbox() {
      return this.el.dataset.comboboxId
        ? (document.getElementById(this.el.dataset.comboboxId) as HTMLInputElement)
        : null
    }

    handleClick(event) {
      const option = event.target.closest(optionSelector)

      if (option) {
        this.setActiveElement(option)
        this.select()
      }
    }

    handleKeyDown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.updateSelection(1)

          break

        case 'ArrowUp':
          event.preventDefault()
          this.updateSelection(-1)

          break

        case 'Enter':
          event.preventDefault()
          this.select()

          break
      }
    }

    handleMouseOver(event) {
      const listbox = this.el
      const hovered = event.target
      const option = hovered.closest(optionSelector)

      if (hovered !== listbox && listbox.contains(hovered) && option) {
        this.setActiveElement(option)
      }
    }

    initActiveOption() {
      const activeElem = this.activeId && this.el.querySelector<HTMLElement>(`[data-id="${this.activeId}"]`)
      const firstElem = this.el.querySelector<HTMLElement>(optionSelector)

      if (activeElem) {
        this.setActiveElement(activeElem)
      } else if (firstElem) {
        this.setActiveElement(firstElem)

        // When defaulting to the first item, unset activeId. This ensures that as options load
        // asynchronously, the first option will remain active.
        this.activeId = undefined
      } else {
        // No options available. Reset the activeId so that when options become available again, the
        // first option will be active.
        this.activeId = undefined
      }
    }

    select() {
      const selected = this.el.querySelector<HTMLElement>('[data-active]')
      const onSelect = selected?.getAttribute('data-on-select')

      if (selected && onSelect) {
        this.liveSocket.execJS(selected, onSelect)

        const combobox = this.getComboxbox()
        if (combobox) {
          combobox.value = ''
        }
      }
    }

    updateSelection(delta: number) {
      const active = this.el.querySelector<HTMLElement>('[data-active]')
      const options = Array.from(this.el.querySelectorAll<HTMLElement>(optionSelector))

      if (active) {
        let nextIndex = options.indexOf(active) + delta
        nextIndex = Math.min(options.length - 1, nextIndex)
        nextIndex = Math.max(0, nextIndex)

        const next = options[nextIndex]

        this.setActiveElement(next)

        if (!isVisibleInContainer(next, this.el)) {
          next.scrollIntoView({ behavior: 'smooth' })
        }
      } else {
        // If there was no active option, then fall back to activating the first
        this.initActiveOption()
      }
    }

    setActiveElement(elem: HTMLElement) {
      for (const e of this.el.querySelectorAll(optionSelector)) {
        e.removeAttribute('data-active')
      }

      elem.setAttribute('data-active', 'true')

      this.activeId = elem.dataset.id
      this.getComboxbox()?.setAttribute('aria-activedescendant', elem.id)
    }
  }
)
